import React from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

interface NavigationItem {
  name: string;
  href: string;
  icon: LucideIcon;
  onAction?: () => void;
}

interface MobileBottomNavProps {
  navigation: NavigationItem[];
  isActive: (href: string) => boolean;
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ navigation, isActive }) => {
  return (
    <nav 
      className="fixed z-50 lg:hidden px-4 pointer-events-none"
      style={{ 
        bottom: 'max(1.25rem, env(safe-area-inset-bottom))', 
        left: 0, 
        right: 0 
      }}
    >
      <div className="flex items-center justify-between px-2 py-2 bg-white/90 backdrop-blur-xl border border-gray-200/80 shadow-[0_8px_30px_rgb(0,0,0,0.08)] rounded-[24px] pointer-events-auto max-w-sm mx-auto">
        {navigation.map((item) => {
          const Icon = item.icon;
          const active = !item.onAction && isActive(item.href);

          const content = (
            <>
              {/* Animated Icon Pill Container */}
              <div 
                className={`relative flex items-center justify-center w-[52px] h-[32px] rounded-full transition-all duration-300 ${
                  active ? 'bg-blue-100 text-blue-600' : 'text-gray-500 group-hover:bg-gray-100/50'
                }`}
              >
                <Icon 
                  className={`h-[20px] w-[20px] transition-transform duration-300 ${
                    active ? 'scale-110' : 'group-active:scale-90 group-active:text-gray-700'
                  }`} 
                  strokeWidth={active ? 2.5 : 2} 
                />
              </div>
              {/* Minimal Label */}
              <span 
                className={`text-[10px] mt-1 font-bold tracking-wide transition-colors duration-300 ${
                  active ? 'text-blue-600' : 'text-gray-500'
                }`}
              >
                {item.name}
              </span>
            </>
          );

          if (item.onAction) {
            return (
              <button
                key={item.name}
                onClick={item.onAction}
                className="flex flex-col items-center justify-center flex-1 min-w-[48px] group transition-transform active:scale-95 pt-1 pb-1 outline-none tap-highlight-transparent"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {content}
              </button>
            );
          }
          
          return (
            <Link
              key={item.href}
              to={item.href}
              className="flex flex-col items-center justify-center flex-1 min-w-[48px] group transition-transform active:scale-95 pt-1 pb-1 outline-none tap-highlight-transparent"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {content}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
